/**
 * Utility function for intentional scroll to top.
 * Use this when you explicitly want to scroll the window to top.
 * 
 * @param smooth - Whether to use smooth scrolling behavior
 * 
 * @example
 * import { scrollToTop } from 'src/utils/scrollHelpers';
 * 
 * const handleNextPage = () => {
 *   scrollToTop(); // Explicitly scroll to top
 * };
 */
export function scrollToTop(smooth = false) {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: smooth ? 'smooth' : 'auto'
  });
}

/**
 * Hook to get a callback that scrolls to top.
 * Useful when you need to pass scroll function as a prop.
 * 
 * @example
 * const scrollTop = useScrollToTop();
 * <Button onClick={scrollTop}>Back to Top</Button>
 */
export function useScrollToTop() {
  return () => scrollToTop();
}

/**
 * Preserve current scroll position before an action (like opening a modal)
 * and restore it after.
 * 
 * @example
 * const { save, restore } = useScrollPreservation();
 * 
 * const openModal = () => {
 *   save();
 *   setModalOpen(true);
 * };
 * 
 * const closeModal = () => {
 *   setModalOpen(false);
 *   restore();
 * };
 */
export function useScrollPreservation() {
  let savedPosition = 0;
  
  return {
    save: () => {
      savedPosition = window.scrollY || window.pageYOffset;
    },
    restore: () => {
      requestAnimationFrame(() => {
        window.scrollTo(0, savedPosition);
      });
    }
  };
}
